import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface GameState {
  // 사용자 정보
  userName: string;
  
  // 게임 시간 관리
  gameStartTime: number | null;
  currentPlayTime: number;
  totalPlayTime: number;
  isGameActive: boolean;
  isGameCompleted: boolean;
  finalPlayTime: number;
  
  // 퀴즈 진행 상황
  completedMonsters: string[];
  answeredQuestions: Record<string, number[]>;
  
  setUserName: (name: string) => void;
  startGame: () => void;
  updatePlayTime: () => void;
  completeQuiz: (monsterType: string, questionIndex: number) => void;
  completeGame: () => void;
  endGame: () => void;
  resetGame: () => void;
  getCompletedMonstersCount: () => number;
  getFormattedPlayTime: () => string;
  getFinalPlayTime: () => string;
}

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      userName: '',
      gameStartTime: null,
      currentPlayTime: 0,
      totalPlayTime: 0,
      isGameActive: false,
      isGameCompleted: false,
      finalPlayTime: 0,
      completedMonsters: [],
      answeredQuestions: {},

      setUserName: (name: string) => {
        set({ userName: name });
      },

      // 게임 시작
      startGame: () => {
        const now = Date.now();
        set({
          gameStartTime: now,
          currentPlayTime: 0,
          isGameActive: true,
        });
      },

      // 현재 플레이 시간 업데이트
      updatePlayTime: () => {
        const { gameStartTime, isGameActive } = get();
        if (isGameActive && gameStartTime) {
          const currentTime = Date.now();
          const playTime = Math.floor((currentTime - gameStartTime) / 1000); 
          set({ currentPlayTime: playTime });
        }
      },

      // 퀴즈 완료
      completeQuiz: (monsterType: string, questionIndex: number) => {
        const { answeredQuestions, completedMonsters } = get();
        
        const existingAnswers = answeredQuestions[monsterType] || [];
        const newAnswers = existingAnswers.includes(questionIndex) 
          ? existingAnswers 
          : [...existingAnswers, questionIndex];
        
        const newAnsweredQuestions = {
          ...answeredQuestions,
          [monsterType]: newAnswers,
        };

        // 완료된 몬스터 확인 (1문제 이상 풀면 완료)
        const newCompletedMonsters = [...completedMonsters];
        if (newAnswers.length >= 1 && !newCompletedMonsters.includes(monsterType)) {
          newCompletedMonsters.push(monsterType);
        }

        set({
          answeredQuestions: newAnsweredQuestions,
          completedMonsters: newCompletedMonsters,
        });
      },

      // 게임 완료 (8개 몬스터 모두 완성)
      completeGame: () => {
        const { currentPlayTime } = get();
        set({
          isGameActive: false,
          isGameCompleted: true,
          finalPlayTime: currentPlayTime,
        });
      },

      // 게임 종료
      endGame: () => {
        const { currentPlayTime, totalPlayTime } = get();
        set({
          isGameActive: false,
          totalPlayTime: totalPlayTime + currentPlayTime,
          currentPlayTime: 0,
        });
      },

      // 게임 리셋
      resetGame: () => {
        set({
          userName: '',
          gameStartTime: null,
          currentPlayTime: 0,
          totalPlayTime: 0,
          isGameActive: false,
          isGameCompleted: false,
          finalPlayTime: 0,
          completedMonsters: [],
          answeredQuestions: {},
        });
      },

      getCompletedMonstersCount: () => {
        return get().completedMonsters.length;
      },

      getFormattedPlayTime: () => {
        const { currentPlayTime } = get();
        const minutes = Math.floor(currentPlayTime / 60);
        const seconds = currentPlayTime % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      },

      getFinalPlayTime: () => {
        const { finalPlayTime } = get();
        const minutes = Math.floor(finalPlayTime / 60);
        const seconds = finalPlayTime % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      },
    }),
    {
      name: 'safety-hero-game-storage',
    }
  )
);
